import Offcanvas from 'react-bootstrap/Offcanvas';
import OffCanvasCloseBtn from 'components/offcanvas/OffCanvasCloseBtn';

//Components
import Form from 'react-bootstrap/Form';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import { IoAdd, IoCheckmark, IoTime, IoTrash } from 'react-icons/io5';
import OffCanvasLoader from 'components/loader/OffCanvasLoader';
import { toast } from 'react-toastify';

//Utils
import axios from 'axios';
import { useEffect, useMemo, useState } from 'react';

/**
 * Gestione pacchetti servizio (Product type = PACKAGE).
 * I servizi inclusi sono salvati nella tabella package_items lato backend.
 * I servizi selezionabili arrivano dai prodotti gia caricati nella pagina Servizi.
 */

// Servizi selezionabili nel pacchetto (servizi prenotabili).
const SELECTABLE_TYPES = ['PRIMARY', 'EXTRA', 'GENERIC'];

let _uid = 0;
const nextUid = () => ++_uid;

// Valori di default (prezzo/durata) presi dal servizio selezionato.
function getProductDefaults(product) {
    if (!product) return { price: '', price2: '', duration: '' };
    // Servizi prenotabili (PRIMARY/EXTRA/GENERIC): la lista espone gia' il prezzo
    // base (prima fascia di mq) come base_price/base_time.
    if (product.base_price !== undefined || product.base_time !== undefined) {
        return { price: product.base_price ?? '', price2: '', duration: product.base_time ?? '' };
    }
    const matrix = product?.price_matrix;
    if (!matrix) return { price: '', price2: '', duration: '' };
    // Prodotti semplici: oggetto { price, discount_price }
    if (!Array.isArray(matrix)) {
        return {
            price: matrix.price ?? '',
            price2: matrix.discount_price ?? '',
            duration: ''
        };
    }
    // Prodotti a matrice: prima riga come riferimento
    const first = matrix[0] || {};
    return {
        price: first.price ?? '',
        price2: first.price2 ?? '',
        duration: first.time ?? ''
    };
}

const emptyRow = () => ({ uid: nextUid(), productId: '', description: '', price: '', price2: '', duration: '' });

function useOffcanvasManagePackage({ products, packageProduct, onUpdated, onDeleted, handleClose }) {

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    // Consegna rapida (priority) del pacchetto, come per i servizi (es. "Consegna in 24h").
    const [priorityActive, setPriorityActive] = useState(false);
    const [priorityLabel, setPriorityLabel] = useState('Consegna in 24h');
    const [priorityPrice, setPriorityPrice] = useState('');
    // Righe servizio: { uid, productId, description, price, price2, duration }
    const [rows, setRows] = useState([emptyRow()]);
    const [isConfirmDelete, setIsConfirmDelete] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const availableProducts = useMemo(
        () => (products || []).filter(p => SELECTABLE_TYPES.includes(p.type) && !p.isSkeleton),
        [products]
    );

    // All'apertura carico il dettaglio del pacchetto (nome, descrizione, servizi inclusi).
    useEffect(() => {
        if (!packageProduct) return;
        setIsConfirmDelete(false);
        setName(packageProduct.description || '');
        setDescription('');
        setPriorityActive(false);
        setPriorityLabel('Consegna in 24h');
        setPriorityPrice('');
        setRows([emptyRow()]);
        setLoading(true);
        axios.get(`/products/${packageProduct.id}`, { params: { detailed: 1 } })
            .then(res => {
                setName(res.data.description || '');
                setDescription(res.data.description_extend || '');
                setPriorityActive(!!res.data.priority_active);
                setPriorityLabel(res.data.priority_label || 'Consegna in 24h');
                setPriorityPrice(res.data.priority_price ?? '');
                const items = res.data.items || [];
                setRows(items.length
                    ? items.map(it => ({
                        uid: nextUid(),
                        productId: it.product_id,
                        description: it.description || '',
                        price: it.price ?? '',
                        price2: it.price2 ?? '',
                        duration: it.duration ?? ''
                    }))
                    : [emptyRow()]
                );
            })
            .finally(() => setLoading(false));
    }, [packageProduct]);

    const addRow = () => setRows(prev => [...prev, emptyRow()]);

    const removeRow = (uid) => setRows(prev => prev.filter(r => r.uid !== uid));

    const changeProduct = (uid, productId) => {
        const product = availableProducts.find(p => String(p.id) === String(productId));
        const defaults = product ? getProductDefaults(product) : { price: '', price2: '', duration: '' };
        setRows(prev => prev.map(r => r.uid === uid
            ? { ...r, productId, description: product?.description || '', ...defaults }
            : r
        ));
    };

    const changeField = (uid, field, value) => {
        setRows(prev => prev.map(r => r.uid === uid ? { ...r, [field]: value } : r));
    };

    // Righe che hanno un servizio selezionato (mostrate nel tab Tariffe).
    const selectedRows = rows.filter(r => r.productId);

    const productOf = (row) => availableProducts.find(p => String(p.id) === String(row.productId));

    // Prezzo/durata "a listino", presi dal DB (price_matrix del servizio, prima fascia).
    // L'override manuale per il pacchetto e' una feature futura (vedi tab Tariffe).
    const rowDbDefaults = (row) => {
        const product = productOf(row);
        return product ? getProductDefaults(product) : { price: '', price2: '', duration: '' };
    };

    const priorityPriceValue = priorityActive ? (parseFloat(priorityPrice) || 0) : 0;

    const totalPrice = selectedRows.reduce((sum, r) => sum + (parseFloat(rowDbDefaults(r).price) || 0), 0) + priorityPriceValue;
    const totalDuration = selectedRows.reduce((sum, r) => sum + (parseFloat(rowDbDefaults(r).duration) || 0), 0);

    const rowDescription = (row) => {
        const product = productOf(row);
        return product?.description || row.description || '';
    };

    const handleSave = (e) => {
        e.preventDefault();
        if (saving) return;
        setSaving(true);
        axios.put(`products/${packageProduct.id}`, {
            description: name,
            description_extend: description,
            // Consegna rapida (priority): se attiva, label e prezzo sono richiesti dal backend.
            priority_active: priorityActive,
            ...(priorityActive ? {
                priority_label: priorityLabel,
                priority_price: priorityPrice === '' ? null : Number(priorityPrice)
            } : {}),
            // Nessun override per ora: il prezzo del pacchetto e' la somma dei prezzi
            // a listino (DB) dei servizi. L'override manuale e' una feature futura.
            items: selectedRows.map(r => ({
                product_id: r.productId,
                price: null,
                price2: null,
                duration: null
            }))
        })
            .then(res => {
                onUpdated && onUpdated(res.data);
                toast.success('Pacchetto salvato con successo!');
            })
            .catch(() => toast.error('Errore durante il salvataggio.'))
            .finally(() => setSaving(false));
    };

    const handleDelete = () => {
        if (!isConfirmDelete) return setIsConfirmDelete(true);
        axios.delete(`products/${packageProduct.id}`)
            .then(() => {
                toast.success('Pacchetto eliminato con successo');
                onDeleted && onDeleted(packageProduct.id);
                handleClose();
            });
    };

    return {
        name, setName,
        description, setDescription,
        priorityActive, setPriorityActive,
        priorityLabel, setPriorityLabel,
        priorityPrice, setPriorityPrice,
        rows, selectedRows,
        availableProducts,
        addRow, removeRow, changeProduct, changeField,
        totalPrice, totalDuration,
        rowDescription, rowDbDefaults,
        isConfirmDelete,
        loading, saving,
        handleSave, handleDelete
    };
}

function OffcanvasManagePackage(props) {

    const { packageProduct, handleClose } = props;

    const {
        name, setName,
        description, setDescription,
        priorityActive, setPriorityActive,
        priorityLabel, setPriorityLabel,
        priorityPrice, setPriorityPrice,
        rows, selectedRows,
        availableProducts,
        addRow, removeRow, changeProduct,
        totalPrice, totalDuration,
        rowDescription, rowDbDefaults,
        isConfirmDelete,
        loading, saving,
        handleSave, handleDelete
    } = useOffcanvasManagePackage(props);

    return (
        <Offcanvas placement="bottom" className="action-sheet" show={!!packageProduct} onHide={handleClose}>
            <Offcanvas.Body>
                <div className="section mt-3 text-center position-relative">
                    <a
                        className="text-25 back-btn-modal text-danger"
                        onClick={handleDelete}
                        style={{ top: -5 }}
                    >
                        {isConfirmDelete ? <IoCheckmark /> : <IoTrash />}
                    </a>
                    <h3>Gestione pacchetto</h3>
                    <OffCanvasCloseBtn handleClose={handleClose} style={{ top: -5 }} />
                </div>

                {loading && <OffCanvasLoader />}

                <div className="section py-2">
                        {/* ---------- INFORMAZIONI ---------- */}
                            <Form className="section px-0 py-2" onSubmit={handleSave}>

                                <div className="form-group boxed custom">
                                    <div className="input-wrapper">
                                        <Form.Label className="label-bordered" htmlFor="package-name">Nome pacchetto</Form.Label>
                                        <Form.Control
                                            type="text"
                                            id="package-name"
                                            minLength="3"
                                            maxLength="100"
                                            value={name}
                                            onChange={e => setName(e.target.value)}
                                            placeholder="Es. Pacchetto Premium"
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="form-group boxed custom">
                                    <div className="input-wrapper">
                                        <Form.Label className="label-bordered" htmlFor="package-description">Descrizione pacchetto</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            id="package-description"
                                            value={description}
                                            onChange={e => setDescription(e.target.value)}
                                        />
                                    </div>
                                </div>

                                <Form.Label className="ms-1 mb-1 text-white">Servizi del pacchetto:</Form.Label>

                                {rows.map(row => (
                                    <div className="d-flex align-items-center mb-1" key={row.uid}>
                                        <div className="form-group boxed custom flex-grow-1 mb-0">
                                            <div className="input-wrapper">
                                                <Form.Select
                                                    className="form-control"
                                                    value={row.productId}
                                                    onChange={e => changeProduct(row.uid, e.target.value)}
                                                >
                                                    <option value="">Seleziona servizio</option>
                                                    {availableProducts.map(p => (
                                                        <option key={p.id} value={p.id}>{p.description}</option>
                                                    ))}
                                                </Form.Select>
                                            </div>
                                        </div>
                                        {rows.length > 1 && (
                                            <a
                                                className="text-danger text-22 ms-2 cursor-pointer"
                                                onClick={() => removeRow(row.uid)}
                                            >
                                                <IoTrash />
                                            </a>
                                        )}
                                    </div>
                                ))}

                                <div className="text-center mt-1 mb-2">
                                    <button type="button" className="btn btn-icon btn-success" onClick={addRow}>
                                        <IoAdd />
                                    </button>
                                </div>

                                {/* Consegna rapida (priority) del pacchetto: come per i servizi (es. "Consegna in 24h") */}
                                <ul className="listview simple-listview no-border mt-1 mb-2 rounded form-group">
                                    <li className="form-control text-white">
                                        <div className="lh-1">Consegna rapida?</div>
                                        <Form.Check
                                            type="switch"
                                            id="package-priority-active"
                                            label=""
                                            checked={priorityActive}
                                            onChange={e => setPriorityActive(e.target.checked)}
                                        />
                                    </li>
                                </ul>

                                {/* {priorityActive && (
                                    <div className="row">
                                        <div className="col ps-1">
                                            <div className="form-group boxed custom">
                                                <div className="input-wrapper">
                                                    <Form.Label className="label-bordered">Etichetta consegna rapida</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        minLength="3"
                                                        maxLength="50"
                                                        value={priorityLabel}
                                                        onChange={e => setPriorityLabel(e.target.value)}
                                                        required
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col pe-1">
                                            <div className="form-group boxed custom">
                                                <div className="input-wrapper not-empty">
                                                    <Form.Label className="label-bordered">Prezzo consegna rapida</Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        min="0"
                                                        value={priorityPrice}
                                                        onChange={e => setPriorityPrice(e.target.value)}
                                                        required
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )} */}

                                <div className="form-group basic">
                                    <button type="submit" className="btn btn-primary btn-block" disabled={saving}>
                                        Salva pacchetto
                                    </button>
                                </div>
                            </Form>

                        {/* ---------- TARIFFE ---------- */}
                        {/* <Tab eventKey="prices" title="Tariffe">
                            <Form className="section px-0 py-2" onSubmit={handleSave}>

                                {selectedRows.length === 0 && (
                                    <div className="text-center text-muted py-3">
                                        Seleziona almeno un servizio nel tab Informazioni.
                                    </div>
                                )}

                                {selectedRows.length > 0 && (
                                    <div className="row mx-auto text-white fw-500 d-none d-md-flex text-center" style={{ marginBottom: '-5px' }}>
                                        <div className="col-md-6 pe-0">Servizio</div>
                                        <div className="col-md-3 pe-0">Prezzo (€)</div>
                                        <div className="col-md-3 pe-0">Durata (min)</div>
                                    </div>
                                )}

                                {selectedRows.map(row => {
                                    const db = rowDbDefaults(row);
                                    const show = (v) => (v !== '' && v !== null && v !== undefined ? v : '-');
                                    return (
                                    <div className="row gy-2 mt-1 mx-auto align-items-center" key={row.uid}>
                                        <div className="col-md-6 col-12 pe-0">
                                            <div className="form-group boxed input-group position-relative pt-0">
                                                <Form.Control
                                                    type="text"
                                                    className="px-2 fw-600"
                                                    value={rowDescription(row)}
                                                    disabled
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-3 col-6 pe-0">
                                            <div className="form-group boxed input-group position-relative pt-0">
                                                <span className="input-group-text px-2 fw-600 input-price-icon">€</span>
                                                <Form.Control
                                                    type="text"
                                                    className="px-2"
                                                    value={show(db.price)}
                                                    disabled
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-3 col-6 pe-0">
                                            <div className="form-group boxed input-group position-relative pt-0">
                                                <span className="input-group-text px-2 fw-600 input-price-icon"><IoTime /></span>
                                                <Form.Control
                                                    type="text"
                                                    className="px-2"
                                                    value={show(db.duration)}
                                                    disabled
                                                    readOnly
                                                />
                                            </div>
                                        </div>

                                        FEATURE FUTURA - override manuale di Prezzo1/Prezzo2/Durata per il pacchetto.
                                            Per ora il prezzo e' quello a listino (DB). Per riabilitarlo:
                                            reintrodurre questi input editabili (con changeField e row.price/price2/duration),
                                            ripristinare le colonne Prezzo2/Durata nell'header e il salvataggio dei valori in handleSave.

                                        <div className="col-md-2 col-4 pe-0">
                                            <div className="form-group boxed input-group position-relative pt-0">
                                                <span className="input-group-text px-2 fw-600 input-price-icon">EUR</span>
                                                <Form.Control type="number" className="px-2" value={row.price} onChange={e => changeField(row.uid, 'price', e.target.value)} />
                                            </div>
                                        </div>
                                        <div className="col-md-2 col-4 pe-0">
                                            <div className="form-group boxed input-group position-relative pt-0">
                                                <span className="input-group-text px-2 fw-600 input-price-icon">EUR</span>
                                                <Form.Control type="number" className="px-2" value={row.price2} onChange={e => changeField(row.uid, 'price2', e.target.value)} />
                                            </div>
                                        </div>
                                        <div className="col-md-2 col-4 pe-0">
                                            <div className="form-group boxed input-group position-relative pt-0">
                                                <span className="input-group-text px-2 fw-600 input-price-icon">T</span>
                                                <Form.Control type="number" className="px-2" value={row.duration} onChange={e => changeField(row.uid, 'duration', e.target.value)} />
                                            </div>
                                        </div>
                                        <div className="col-md-2 col-12">
                                            <button type="button" style={{ height: '46px' }} className="btn btn-danger text-xl p-0 w-100 rounded-2" onClick={() => removeRow(row.uid)}>
                                                <IoTrash className="m-0" />
                                            </button>
                                        </div>
                                       
                                    </div>
                                    );
                                })}

                                Consegna rapida (priority): se attiva, il suo prezzo entra nella tariffa
                                {priorityActive && (
                                    <div className="row gy-2 mt-1 mx-auto align-items-center">
                                        <div className="col-md-6 col-12 pe-0">
                                            <div className="form-group boxed input-group position-relative pt-0">
                                                <Form.Control
                                                    type="text"
                                                    className="px-2 fw-600"
                                                    value={priorityLabel || 'Consegna in 24h'}
                                                    disabled
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-3 col-6 pe-0">
                                            <div className="form-group boxed input-group position-relative pt-0">
                                                <span className="input-group-text px-2 fw-600 input-price-icon">€</span>
                                                <Form.Control
                                                    type="text"
                                                    className="px-2"
                                                    value={priorityPrice !== '' && priorityPrice !== null && priorityPrice !== undefined ? priorityPrice : '-'}
                                                    disabled
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-3 col-6 pe-0">
                                            <div className="form-group boxed input-group position-relative pt-0">
                                                <span className="input-group-text px-2 fw-600 input-price-icon"><IoTime /></span>
                                                <Form.Control
                                                    type="text"
                                                    className="px-2"
                                                    value="-"
                                                    disabled
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {(selectedRows.length > 0 || priorityActive) && (
                                    <div className="d-flex justify-content-between fw-600 text-white px-1 mt-3">
                                        <div>Prezzo totale: € {totalPrice.toFixed(2)}</div>
                                        <div>Durata totale: {totalDuration} min</div>
                                    </div>
                                )}

                                <div className="form-group basic mt-2">
                                    <button type="submit" className="btn btn-primary btn-block" disabled={saving}>
                                        Salva pacchetto
                                    </button>
                                </div>
                            </Form>
                        </Tab> */}
                </div>
            </Offcanvas.Body>
        </Offcanvas>
    );
}

export default OffcanvasManagePackage;
